import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
	data,
	type LoaderFunctionArgs,
	useLoaderData,
	useSearchParams,
} from 'react-router'
import StackedCardsComponent
	from '#app/components/blog/stacked-cards-layout.tsx'
import Instagram from '#app/components/logos/instagram.tsx'
import HorizontalScrollGallery
	from '#app/components/marketing/horizontal-scroll-gallery.tsx'
import MarketingImage from '#app/components/marketing/image.tsx'
import { LandingSection } from '#app/components/marketing/landing-section.tsx'
import SplitText from '#app/components/motion/split-text.tsx'
import TickerOverflowLoop from '#app/components/motion/ticker-overflow.tsx'
import { Button } from '#app/components/ui/button.tsx'
import {
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
} from '#app/components/ui/pagination.tsx'
import { Text } from '#app/components/ui/typography.tsx'
import { usePublicContext } from '#app/routes/($lang)+/__public-context.tsx'
import { useMainStore } from '#app/stores/main'
import { cachifiedWithMetrics } from '#app/utils/cache-metrics.server.ts'
import { BASE_URL } from '#app/utils/config.ts'
import { prisma } from '#app/utils/db.server.js'
import { extendedTheme } from '#app/utils/extended-theme.ts'
import { LocalizedLink } from '#app/utils/i18n.tsx'
import {
	generateMetaTranslationLoader,
	i18next,
} from '#app/utils/i18next.server.js'
import { useDesktop } from '#app/utils/media-query.ts'
import { generateTranslatedMeta } from '#app/utils/meta.ts'
import { cn, getPostImgSrc } from '#app/utils/misc.tsx'
import { createPerformanceTimer } from '#app/utils/timing.server.ts'
import routes from '#routes/index.tsx'

const POSTS_PER_PAGE = 6
const OFFSET_ACCOUNT_FOR_FIRST_POST_STYLING = 1

interface BlogSearchParams {
	page?: number
	categoryCode?: string
}

export async function loader({ request }: LoaderFunctionArgs) {
	const timer = createPerformanceTimer()
	timer.start('total', 'Total loader execution')

	const locale = await i18next.getLocale(request)

	const url = new URL(request.url)

	// Pagination
	const page = Math.floor(Number(url.searchParams.get('page')) || 1)
	let skip = (page - 1) * POSTS_PER_PAGE

	// Category filter
	const category = url.searchParams.get('category') ?? null

	// Create cache key based on locale, page, and category
	const cacheKey = `magazine-list:${locale}:page-${page}:category-${category || 'all'}`

	// Fetch data with caching (15 min TTL)
	const cachedData = await cachifiedWithMetrics({
		key: cacheKey,
		ttl: 1000 * 60 * 15, // 15 minutes
		route: 'magazine.index',
		async getFreshValue() {
			timer.start('db-count', 'Count posts')
			const amountOfPostsWithLocaleAndCategory = await prisma.post.count({
				where: {
					removedAt: null,
					published: true,
					translations: {
						some: {
							languageCode: locale,
						},
					},
					categories: category
						? {
								some: {
									code: category,
								},
							}
						: undefined,
				},
			})
			timer.end('db-count')

			// Adjust the skipping so that even with pages that don't exist, we show the last page posts.
			let adjustedSkip = skip
			if (skip > amountOfPostsWithLocaleAndCategory) {
				adjustedSkip =
					Math.max(amountOfPostsWithLocaleAndCategory, POSTS_PER_PAGE) -
					POSTS_PER_PAGE
			}

			timer.start('db-posts', 'Fetch posts')
			// Gather the posts that meet the requirements. Filtered by pagination and/or category.
			const posts = await prisma.post.findMany({
				where: {
					removedAt: null,
					published: true,
					translations: {
						some: {
							languageCode: locale,
						},
					},
					categories: category
						? {
								some: {
									code: category,
								},
							}
						: undefined,
				},
				orderBy: {
					publishedAt: 'desc',
				},
				include: {
					images: {
						where: {
							type: 'cover',
						},
						take: 1,
						select: {
							id: true,
							// Only select ID - blob will be loaded on-demand via resource route
						},
					},
					translations: {
						where: {
							languageCode: locale,
						},
						take: 1,
					},
				},
				skip: adjustedSkip,
				take: POSTS_PER_PAGE,
			})
			timer.end('db-posts')

			timer.start('db-categories', 'Fetch categories')
			// Gather the available categories to show the filter.
			const categories = await prisma.postCategory.findMany({
				include: {
					translations: {
						where: {
							languageCode: locale,
						},
					},
					_count: {
						select: {
							posts: true,
						},
					},
				},
				where: {
					posts: {
						some: {
							published: true,
						},
					},
				},
			})
			timer.end('db-categories')

			return {
				firstPost: adjustedSkip === 0 ? posts[0] : undefined,
				posts: (adjustedSkip === 0 ? posts.slice(1) : posts).map((post) => ({
					...post,
					translations: post.translations.map((translation) => {
						const firstPTagIndex = translation.content.indexOf('<p>')
						const firstPCloseTagIndex = translation.content.indexOf('</p>')

						return {
							...translation,
							content: translation.content.substring(
								firstPTagIndex,
								firstPCloseTagIndex + 4,
							),
						}
					}),
				})),
				locale,
				categories,
				pagination: {
					currentPage: Math.floor(adjustedSkip / POSTS_PER_PAGE + 1),
					amountOfPages: Math.ceil(
						amountOfPostsWithLocaleAndCategory / POSTS_PER_PAGE,
					),
					skipping: adjustedSkip,
				},
			}
		},
	})

	const meta = await generateMetaTranslationLoader({
		request,
		namespace: 'magazine',
		keys: {
			title: 'title',
			description: 'description',
			canonical: `${BASE_URL}${routes.public.blog.index.route({})}`,
		},
	})

	timer.end('total')

	return data(
		{
			...cachedData,
			meta,
		},
		{
			headers: {
				'Server-Timing': timer.toServerTimingHeader(),
			},
		},
	)
}

export const meta = generateTranslatedMeta

export default function Blog() {
	const [searchParams, setSearchParams] = useSearchParams()
	const { t } = useTranslation(['common', 'magazine'])
	const backgroundColor = useMainStore.use.backgroundColor()
	const { posts, locale, categories, pagination, firstPost } =
		useLoaderData<typeof loader>()
	const isDesktop = useDesktop()

	const {
		navigation: { route },
	} = usePublicContext()

	useEffect(() => {
		route.setContent([
			{
				label: `Magazine.`,
			},
		])
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const handleSearchParms = ({ toggle }: { toggle: BlogSearchParams }) => {
		// Copy the current search params
		const params = searchParams

		if (toggle.categoryCode) {
			// Currently filtered category.
			const thisCategoryFiltered =
				searchParams.get('category') === toggle.categoryCode

			// If the search params contain this item, and we're clicking on it again, we want to delete it. Else we set this category as the filtered one.
			if (thisCategoryFiltered) params.delete('category')
			else params.set('category', toggle.categoryCode)

			params.delete('page')
		}

		if (toggle.page) {
			params.set('page', String(toggle.page))

			// If we're going to page 1, we can delete the param
			if (toggle.page === 1) {
				params.delete('page')
			}
		}

		// set the new params
		setSearchParams(params)
	}

	return (
		<>
			{(firstPost || posts.length > 0) && (
				<section className="container mb-sm grid grid-cols-12 px-sm py-md md:mb-md lg:mb-lg xl:mb-xxl 2xl:mb-xxxl">
					{categories.length > 0 && (
						<div
							className={
								'col-span-full mx-auto flex max-w-xl flex-wrap items-center justify-center gap-3 self-center py-xxl lg:max-w-4xl'
							}
						>
							{categories.map((category, index) => {
								const categoryTranslation = category.translations.find(
									(lang) => lang.languageCode === locale,
								)
								if (!categoryTranslation) return null

								return (
									<button
										onClick={() => {
											handleSearchParms({
												toggle: {
													categoryCode: category.code,
												},
											})
										}}
										className={cn(
											'inline-flex font-serif !font-light lowercase',
											'text-h4 md:text-h3 lg:text-h1',
											searchParams.has('category') &&
												searchParams.get('category') === category.code &&
												'!font-bold',
										)}
										key={category.id}
									>
										<SplitText delay={index * 0.1} as={'span'}>
											{categoryTranslation.name}
										</SplitText>{' '}
										<Text
											as={'span'}
											className={cn(
												'align-super font-serif !font-extralight',
												'text-h6 md:text-h5 xl:text-h4',
											)}
										>
											<SplitText as={'span'} delay={0.2 + index * 0.1}>
												({category._count.posts})
											</SplitText>
										</Text>
									</button>
								)
							})}
						</div>
					)}

					<div className={'col-span-full'}>
						<LandingSection borderToBorderOnX className={'!mix-blend-normal'}>
							<div className="flex flex-col justify-between gap-xxl pt-xxl text-brand-dusk md:gap-xxxl md:pt-xxxl">
								{firstPost && (
									<div
										className={
											'col-span-full grid grid-cols-5 gap-md md:gap-xl'
										}
									>
										<div
											className={'col-span-full md:col-span-3 2xl:col-span-2'}
										>
											<LocalizedLink to={routes.public.blog.index.route({})}>
												<Text
													as={'h2'}
													className="tracking-[0.2em] text-brand-dusk"
												>
													<span className="mr-xxs">/</span>
													magazine
													<span className="ml-xxs">/</span>
												</Text>
											</LocalizedLink>

											<div
												className={'mt-[5vh] flex flex-col gap-sm lg:gap-md'}
											>
												<SplitText
													element={'words'}
													as={'h3'}
													delay={0.2}
													className="line-clamp-4 font-serif text-h3 !font-light lg:line-clamp-3 lg:text-h1"
												>
													{firstPost.translations[0]!.title}
												</SplitText>
												<p
													dangerouslySetInnerHTML={{
														__html: `${firstPost.translations[0]!.content.substring(0, 250)}...`,
													}}
													className="line-clamp-5 font-extralight lg:line-clamp-3"
												></p>
												<Button
													asChild
													variant={'link'}
													size={'sm'}
													className={
														'self-end justify-self-end lowercase tracking-[0.2em] text-brand-earth'
													}
												>
													<LocalizedLink
														to={routes.public.blog.post.route({
															slug: firstPost.translations[0]!.slug,
														})}
													>
														{t('common:actions.viewMore')}
													</LocalizedLink>
												</Button>
											</div>
										</div>
										<div
											className={'col-span-full md:col-span-2 2xl:col-span-3'}
										>
											<MarketingImage
												imageClassName="block max-h-[300px] md:min-h-[500px] min-h-[300px]"
												src={getPostImgSrc(firstPost.images[0]!.id)}
											/>
										</div>
									</div>
								)}
								<StackedCardsComponent
									indexOffset={
										pagination.skipping +
										(pagination.currentPage === 1
											? OFFSET_ACCOUNT_FOR_FIRST_POST_STYLING
											: 0)
									}
									cards={posts.map((article) => ({
										title: article.translations[0]!.title,
										description: article.translations[0]!.content,
										slug: article.translations[0]!.slug,
										link: routes.public.blog.post.route({
											slug: article.translations[0]!.slug,
										}),
										image: getPostImgSrc(article.images[0]!.id),
									}))}
								/>
							</div>
						</LandingSection>
					</div>

					{/* Pagination */}
					<Pagination className={'col-span-full'}>
						<PaginationContent>
							{pagination.currentPage - 1 >= 1 && (
								<PaginationItem>
									<PaginationPrevious
										aria-disabled={pagination.currentPage === 1}
										to={'#'}
										onClick={() => {
											handleSearchParms({
												toggle: {
													page: pagination.currentPage - 1,
												},
											})
										}}
									/>
								</PaginationItem>
							)}

							{pagination.amountOfPages > 3 ? (
								<>
									{pagination.currentPage - 2 >= 1 && (
										<PaginationItem>
											<PaginationEllipsis />
										</PaginationItem>
									)}
									{new Array(pagination.amountOfPages)
										.fill(null)
										.map((v, index) => index + 1)
										.slice(
											Math.max(
												0,
												Math.min(
													pagination.currentPage - 2,
													pagination.currentPage - 1,
												),
											),
											Math.min(
												pagination.amountOfPages,
												pagination.currentPage + 1,
											),
										)
										.map((v) => (
											<PaginationLink
												isActive={pagination.currentPage === v}
												key={v}
												to={'#'}
												onClick={() => {
													handleSearchParms({
														toggle: {
															page: v,
														},
													})
												}}
											>
												{v}
											</PaginationLink>
										))}
									{pagination.currentPage + 2 <= pagination.amountOfPages && (
										<PaginationItem>
											<PaginationEllipsis />
										</PaginationItem>
									)}
								</>
							) : (
								new Array(pagination.amountOfPages)
									.fill(null)
									.map((v, index) => (
										<PaginationLink
											isActive={pagination.currentPage === index + 1}
											key={index}
											to={'#'}
											onClick={() => {
												handleSearchParms({
													toggle: {
														page: index + 1,
													},
												})
											}}
										>
											{index + 1}
										</PaginationLink>
									))
							)}

							{pagination.currentPage + 1 <= pagination.amountOfPages && (
								<PaginationItem>
									<PaginationNext
										aria-disabled={
											pagination.currentPage === pagination.amountOfPages
										}
										to={'#'}
										onClick={() => {
											handleSearchParms({
												toggle: {
													page: pagination.currentPage + 1,
												},
											})
										}}
									/>
								</PaginationItem>
							)}
						</PaginationContent>
					</Pagination>
				</section>
			)}

			<LandingSection
				borderToBorderOnX
				backgroundColor={extendedTheme.colors.brand.earth.DEFAULT}
				className={'mb-[5vh] text-white'}
			>
				<div className={'flex flex-col gap-y-[15vh] overflow-x-hidden'}>
					<div className={'container flex flex-col gap-[10vh]'}>
						<Text
							as={'h2'}
							size={'pageTitle'}
							weight={'light'}
							className={'uppercase'}
							type={'serif'}
						>
							<SplitText as={'span'}>
								{t('magazine:press.featured.1')}
							</SplitText>
							<span className={'block translate-x-[3vw]'}>
								<SplitText delay={0.2} as={'span'}>
									{t('magazine:press.featured.2')}
								</SplitText>
							</span>
						</Text>

						<div className={''}>
							<HorizontalScrollGallery />
						</div>
					</div>

					<div
						className={
							'grid grid-flow-row grid-cols-10 gap-y-sm xl:grid-cols-12'
						}
					>
						<Text
							className={'col-start-2 -col-end-2 row-start-1'}
							as={'p'}
							weight={'extralight'}
							type={'serif'}
							size={'h4'}
						>
							<span className={'block'}>
								<SplitText as={'span'}>{t('magazine:beriestain.01')}</SplitText>
							</span>
							<span className={'block'}>
								<SplitText as={'span'}>
									{t('magazine:beriestain.012')}
								</SplitText>
							</span>
						</Text>

						<div
							className={
								'col-span-full col-start-1 row-span-4 row-start-2 mt-md sm:col-span-8 lg:col-span-5 lg:mt-xxl xl:col-span-6'
							}
						>
							<MarketingImage
								className={'h-[80vw] md:h-[50vw] lg:h-[30vw]'}
								imageClassName={'object-[50%,28%]'}
								src={'/img/magazine/REVISTA-AD.webp'}
							/>
						</div>

						<div
							className={cn(
								'col-span-full col-start-2 -col-end-1 row-span-7 row-start-7',
								'md:col-start-4',
								'lg:col-start-7 lg:row-start-3',
								'xl:col-start-8 xl:-col-end-2',
								'2xl:-col-end-3',
							)}
						>
							<div
								className={
									'flex flex-col justify-between gap-[15vh] border-l pl-md xl:gap-[18vh] xl:pl-lg'
								}
							>
								<Text
									className={'col-start-2 -col-end-2 row-start-1'}
									as={'p'}
									weight={'extralight'}
									type={'serif'}
									size={'h4'}
								>
									<SplitText as={'span'}>
										{t('magazine:beriestain.02')}
									</SplitText>
								</Text>
								<MarketingImage
									className={'h-[50vh] lg:h-[25vh] xl:h-[50vh]'}
									imageClassName={'object-[50%,40%]'}
									src={'/img/magazine/REVISTA-MANERA.webp'}
								/>
							</div>
						</div>

						<div
							className={cn(
								'col-start-3 -col-end-3 row-start-[16]',
								'md:col-start-2 md:col-end-6 md:row-start-[15]',
								'lg:col-start-3 lg:row-start-9',
								'xl:col-start-4 xl:col-end-7',
							)}
						>
							<a
								href={'https://www.instagram.com/reel/DBeDW6DRpX4/'}
								target={'_blank'}
								rel={'noopener noreferrer'}
								className={'relative'}
							>
								<Instagram className="pointer-events-none absolute left-1/2 top-[42.5%] z-10 w-[15%] -translate-x-1/2 fill-white" />
								<MarketingImage
									src={'/img/magazine/sapienstone-wallpapermag.webp'}
								/>
							</a>
						</div>
					</div>

					<div className={'flex flex-col gap-y-[7vh] overflow-hidden'}>
						<div className={'container relative mx-auto max-w-[1920px]'}>
							<Text
								className={`bg-gradient-to-r from-white text-center uppercase to-[${backgroundColor}] bg-clip-text text-transparent`}
								as={'h2'}
								weight={'extralight'}
								type={'serif'}
								size={'h1'}
							>
								{t('magazine:showcases.title')}
							</Text>
						</div>
						<TickerOverflowLoop
							gap={isDesktop ? 70 : 50}
							items={[
								'/img/magazine/logos/1-MAISON&OBJET.png',
								'/img/magazine/logos/2-COVERINGS.webp',
								'/img/magazine/logos/3-SALONE.webp',
								'/img/magazine/logos/4-SICAM.png',
								'/img/magazine/logos/5-CERSAIE.png',
								'/img/magazine/logos/6-CASADECOR.png',
								'/img/magazine/logos/7-ESPIRITMUEBLE.png',
								'/img/magazine/logos/8-MIAD.png',
								'/img/magazine/logos/9-EQUIPHOTEL_.png',
								'/img/magazine/logos/10-AREA30.png',
								'/img/magazine/logos/11-FERIAHABITAT.png',
								'/img/magazine/logos/12-INTERZUM.png',
							].map((item) => (
								<img
									src={item}
									key={item}
									className={
										'max-w-[20vw] min-[450px]:max-w-[15vw] md:max-w-[10vw] xl:max-w-[6vw] 2xl:max-w-[5vw]'
									}
									alt={item}
								/>
							))}
						/>
					</div>
				</div>
			</LandingSection>
		</>
	)
}
